import { ZCounter } from "components/ZCounter";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { Z } from "models/common/components";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button, Col, Dropdown, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";

export enum FilterType {
    Dropdown,
    Checkbox,
    MultiSelect,
}

export type FilterState = Record<string, any>;

type BaseFilter<T> = {
    label: string;
    filterName: string;
}

type BooleanFilter = BaseFilter<boolean> & {
    type: 'boolean';
    defaultValue: boolean;
    onFilterChange: (e: boolean) => void
}

type SingleSelectFilter<T> = BaseFilter<T> & {
    type: 'single-select';
    options?: T[];
    display: (option: T) => string; // What is shown to the user
    value: (option: T) => any; // The underlying value that actually gets sent
    defaultValue?: T;
    hasNoneOption?: boolean; // Can this filter be nothing? Or does it have to have a value?
    onFilterChange?: (e?: T) => void
}

type MultiSelectFilter<T> = BaseFilter<T[]> & {
    type: 'multi-select';
    options?: T[];
    display: (option: T) => string; // What is shown to the user
    value: (option: T[]) => any; // The underlying value that actually gets sent
    defaultValue?: T[];
    hasNoneOption?: boolean; // Can this filter be nothing? Or does it have to have a value?
    onFilterChange?: (e?: T) => void,
}

type DateSelectFilter = BaseFilter<Date> & {
    type: 'date-select';
    options?: Date;    
    value: (option: Date) => Date; // The underlying value that actually gets sent
    defaultValue?: Date;
    hasNoneOption?: boolean; // Can this filter be nothing? Or does it have to have a value?
    onFilterChange: (e: Date|null) => void,
}

type BooleanSelectFilter = SingleSelectFilter<boolean> & {
    onFilterChange: (e: boolean) => void
}

export type AnyFilter = BooleanFilter | SingleSelectFilter<any> | MultiSelectFilter<any> | DateSelectFilter;

export function createBooleanFilter(config: Omit<BooleanFilter, 'type'>): BooleanFilter {
    return { ...config, type: 'boolean' } as BooleanFilter;
}

export function createBooleanSelectFilter(config: Omit<SingleSelectFilter<boolean>, 'type' | 'display' | 'options' | 'value'>): BooleanSelectFilter {
    return { ...config, type: 'single-select', options: [true, false], value: (e) => e, display: (e) => e ? 'Yes' : 'No' } as BooleanSelectFilter;
}

export function createSingleSelectFilter<T>(config: Omit<SingleSelectFilter<T>, 'type'>): SingleSelectFilter<T> {
    return { ...config, type: 'single-select' } as SingleSelectFilter<T>;
}

export function createMultiSelectFilter<T>(config: Omit<MultiSelectFilter<T>, 'type'>): MultiSelectFilter<T> {
    return { ...config, type: 'multi-select' } as MultiSelectFilter<T>;
}

export function createDateSelectFilter(config: Omit<DateSelectFilter, 'type'>): DateSelectFilter {
    return { ...config, type: 'date-select' } as DateSelectFilter;
}

export type FilterProps = {
    onFilterApplied: (f: FilterState) => void;
    onFilterCleared: () => void;
    filters: AnyFilter[];
    onOpen: () => void;
};

export interface FilterForwardRef {
    getFilters: () => FilterState;
    getAppliedFilters: () => FilterState;
    applyFilters: () => FilterState;
    saveFilters: () => Map<string, any>;
    loadFilters: (state: Map<string, any>) => void;
}

const Filter = forwardRef<FilterForwardRef | undefined, FilterProps>(({filters, onFilterApplied, onFilterCleared, onOpen}: FilterProps, ref) => {
    useImperativeHandle(ref, () => ({
        /**
         * Return the filter state whether it's been applied or not.
         * @returns 
         */
        getFilters() {
            return mapStateToFilterState(filterState);
        },
        /**
         * Return only the filter state that has been applied (ie the state that filters was in when applyFilters() was called last)
         */
        getAppliedFilters() {
            return mapStateToFilterState(appliedFilterState);
        },
        /**
         * Use this method if filters are being sent to an endpoint but the applyFilters() method isn't called
         * to let the filter component know they've been applied, this is so the warning that some filters 
         * aren't applied won't appear erroneously.
         */
        applyFilters() {
            setAppliedFilterState(filterState);
            return filterState;
        },

        saveFilters() {
            return appliedFilterState;
        },

        loadFilters(state: Map<string, any>) {
            setFilterState(s => {
                applyFilters(state);
                return new Map(state);
            });
        }
    }));

    const [filterMenuOpen, setFilterMenuOpen] = useState(false);

    const [filterState, setFilterState] = useState(new Map<string, any>()); // This state is what holds the current values of each filter. NOTE: It holds the VALUE, not the OPTION. So if value is the id of the option, it will store a mapping from filter to id.
    
    const [appliedFilterState, setAppliedFilterState] = useState(new Map<string, any>());

    const activeFilterCount = useMemo<number>(() => {
        let count = 0;
        // Iterate over the filter state and count up how many filters have been selected
        filterState.forEach((value, key) => {
            const filter = getFilterFromFilterName(key);
            if (filter !== undefined) {
                switch(filter.type) {
                    case "boolean": // Don't count false to the filter count for checkboxes
                        if (value === true) {
                            count++;
                        }
                        break;
                    case "single-select":
                 
                    case "multi-select": // If there is any value, add to filter count
                        if (value !== undefined) {
                            count++;
                        }
                        break;
                    case "date-select":
                        if (value !== undefined){                 
                            count++;
                        }
                        break;
                }
            } else {
                throw `Filter name ${key} doesn't match any filters!`;
            }
        });

        return count;
    }, [filterState]);

    // This useEffect is triggered when filters change. We want to update our internal state as necessary to reflect changes
    // in the filters prop (if any).
    useEffect(() => {
        let newFilterState = new Map(filterState);
        newFilterState.forEach((val, key) => {
            // If the filter state has entries that no longer exist in the filters list, delete them
            if (!filters.find(f => f.filterName === key)) {
                newFilterState.delete(key);
            }
        });

        filters.forEach(f => {
            if (newFilterState.has(f.filterName)) {
                const storedValue = newFilterState.get(f.filterName);
                switch(f.type) {
                    case "boolean":
                        // If for some reason we aren't storing a boolean when the filter is a boolean type (could happen if someone
                        // changes the filter type to boolean for some reason?), then we reset the stored value.
                        if (typeof storedValue !== "boolean") {
                            newFilterState.set(f.filterName, getInitialFilterValue(f, false));
                        }
                        break;
                    case "single-select":                
                    case "multi-select":
                        // If undefined was selected already and this is a valid option (due to a none option being possible),
                        // don't change anything
                        if (f.hasNoneOption && storedValue === undefined) {
                            break;
                        }
                        const foundOption = f.options?.find(opt => opt === storedValue);
                        // If we don't have a matching option in the new filter, we will set a new value in the state
                        if (foundOption === undefined) {
                            const defaultValue = getInitialFilterValue(f, false);
                            if (!f.options?.find(opt => opt === defaultValue)) {
                                console.warn("Default value doesn't match any options in the filter! Ignoring default value...");
                                newFilterState.set(f.filterName, getInitialFilterValue(f, true)); 
                            } else {
                                newFilterState.set(f.filterName, defaultValue);
                            }
                        }
                        break;
                    case "date-select":
                        
                         //   newFilterState.set(f.filterName,  undefined);
                        break;
                }
            } else {
                newFilterState.set(f.filterName, getInitialFilterValue(f, false));
            }
        });

        setFilterState(newFilterState);
    }, [filters]);

    const dropdownHasWarning = useMemo<boolean>(() => {
        for (let entry of Array.from(filterState.entries())) {
            const key = entry[0];
            const val = entry[1];
            
            if (appliedFilterState.get(key) !== val) {
                return true;
            }
        }
        return false;
    }, [filterState, appliedFilterState]);

    useEffect(() => {
        const initialState = getInitialState();
        setFilterState(initialState);
        setAppliedFilterState(initialState);
    }, []);

    function setFilterStateValue(f: AnyFilter, val: any) {       
        const newFilterState = new Map(filterState);  // Clone the current map
        newFilterState.set(f.filterName, val);        // Modify the cloned map
        setFilterState(newFilterState);               // Update state with the new map
    }

    // Returns the default value for a given filter, will return filter.defaultValue if it's defined
    // Otherwise, will return undefined if filter.hasNoneOption is true, or filter.options[0] if false
    function getInitialFilterValue(filter: AnyFilter, ignoreDefaultValue: boolean = false) {
        switch(filter.type) {
            case "boolean":
                return ignoreDefaultValue ? false : filter.defaultValue;
            case "single-select":           
            case "multi-select":
                if (!ignoreDefaultValue && filter.defaultValue !== undefined) {
                    return filter.defaultValue;
                } else {
                    if (filter.hasNoneOption === true || filter.options === undefined || filter.options.length === 0) {
                        return undefined;
                    } else {
                        return filter.options.at(0);
                    }
                }
            case "date-select":
                return undefined;   
        }
    }
    
    function getInitialState(ignoreDefaultValue: boolean = false): Map<string, any> {
        const initialFilterState = new Map<string, any>();
        filters.forEach(f => {
            // If there's no default value and it's a single or multi select we need to set the state to options[0] if hasNoneOption is false
            initialFilterState.set(f.filterName, getInitialFilterValue(f, ignoreDefaultValue));
        });

        return initialFilterState;
    }

    function clearFilters() {
        const initialState = getInitialState(true);
        // We do it this way since if we just set the state, it won't update by the time we call applyFilters
        setFilterState(initialState);
        onFilterCleared();
     //   applyFilters(initialState);
    }

    function applyFilters(state: Map<string, any>) {
        setFilterMenuOpen(false);

        setAppliedFilterState(new Map(state));
        onFilterApplied(mapStateToFilterState(new Map(state)));
    }

    function mapStateToFilterState(state: Map<string, any>): FilterState {
        const filterStateArray = Array.from(state.entries());

        // Mapping the filter state to an object to return to the caller
        var filterObj = Object.assign({}, ...filterStateArray.map(item => {
            const filter = getFilterFromFilterName(item[0]);
            if (filter !== undefined && filter !== null) {
                if (item[1] !== undefined && item[1] !== null) {
                    const value = 'value' in filter ? filter.value(item[1]) : item[1];
                    return ({ [filter.filterName]: value });
                }
            } else {
                throw `Filter name ${item[0]} doesn't match any filters!`;
            }
        }));

        return filterObj;
    }
    
    function getFilterFromFilterName(filterName: string) {
        return filters.find(f => f.filterName === filterName);
    }

    function getFilterComponent(filter: AnyFilter) {
        const storedValue = filterState.get(filter.filterName);
        const hasWarning = storedValue !== appliedFilterState.get(filter.filterName);
        switch(filter.type) {
            case "single-select":
                return (
                    <Row key={filter.filterName}>
                        <Col xs={5} className="align-items-center d-flex">{filter.label}</Col>
                        <Col xs={7}>
                            <ZSingleSelectDropdown
                                options={filter.options ?? []}
                                id={filter.filterName}
                                placeholder=""
                                canClear
                                optionKey={(o) => {
                                    // if (filter.filterName === "departmentId" || filter.filterName === "subdepartmentIds") console.log("Option:", o, "Key:", filter.options?.indexOf(o) ?? `${filter.filterName}-${filter.display(o)}-${filter.value(o)}`, "Options:", filter.options, "Filter:", filter, "Filter State:", filterState, "Stored Value:", storedValue, "Index of Option:", filter.options?.indexOf(o));
                                    const optionKey = filter.display(o) + "-" + filter.value(o);
                                    if (optionKey === "-1") {
                                        console.log("OPTION: ", o);
                                        console.log("FILTER: ", filter);
                                        console.log("FILTER OPTIONS: ", filter.options);
                                    }
                                    return optionKey;
                                }}
                                display={(o) => filter.display(o)}
                                value={storedValue}                               
                                onSelectionChange={(newOpt) => {
                                    setFilterStateValue(filter, newOpt !== undefined ? newOpt : undefined);
                                    filter.onFilterChange && filter.onFilterChange(newOpt);
                                }} 
                            />
                        </Col>
                        {/* <Col xs={1} className="align-items-center d-flex ps-0">
                            {hasWarning ? <FilterInformationComponent /> : <></>}
                        </Col> */}
                    </Row>
                );
            case "boolean":
                return (
                    <Row className="align-items-center">
                        <Col xs={4} className="d-flex align-items-center">{filter.label}</Col>
                        <Col xs={8} className="d-flex align-items-center">
                            <Form.Check className="d-flex" checked={storedValue} onChange={(e) => { setFilterStateValue(filter, e.target.checked); filter.onFilterChange(e.target.checked); }}></Form.Check>
                        </Col>
                        {/* <Col xs={1} className="d-flex ps-0">
                            {hasWarning ? <FilterInformationComponent /> : <></>}
                        </Col> */}
                    </Row>
                );
            case "date-select":   
                return (
                    <Row >
                        <Col xs={5} className="d-flex align-items-center">{filter.label}</Col>
                        <Col xs={7}>
                            <ZDateTimePicker                                
                                value={storedValue == undefined? null: storedValue}
                                hasTime={false}                                
                                onChanged={(e) => {                      
                                    if(e == null){
                                        setFilterStateValue(filter, undefined); 
                                        filter.onFilterChange(null);
                                    } else {
                                        let dateSt: Date|null  = new Date(e!.toDateString());
                                       
                                        setFilterStateValue(filter, dateSt); 
                                        filter.onFilterChange(dateSt);
                                    }
                                }}
                                isOnPopup={true}
                                />
                        </Col>
                    </Row>
                );
            case "multi-select":
                return (
                    <></>
                );
            default: throw "Filter type doesn't match any known filters, perhaps a new type of filter was added without adding a component for it?";
        }
    }

    const FilterMenu = <Dropdown.Menu>
        <div className="m-2 gap-2 d-flex flex-column">
            {filters.map((f) => (
                getFilterComponent(f)
            ))}
            <Row>
                <Col xs={3} />
                <Col xs={9} className="d-flex flex-row-reverse">
                    <Button variant="primary" className="btn-icon custom-btn-icon" style={{width:"auto"}} onClick={() => {
                        applyFilters(filterState);
                    }}>Apply</Button>
                    <Button variant="light" className="btn-icon custom-btn-icon me-2" style={{width:"auto"}} onClick={clearFilters}>Clear</Button>
                </Col>
            </Row>
        </div>
    </Dropdown.Menu>

    const FilterButton = 
        <Dropdown.Toggle variant="light">
            Filter <Z.Counter counter={activeFilterCount} />
        </Dropdown.Toggle>

    return (
            <Dropdown
                show={filterMenuOpen}
                autoClose="outside"
                onToggle={() => {setFilterMenuOpen(!filterMenuOpen); onOpen();}}
                className="btn-icon me-1 d-inline-flex align-items-center main-search"
            >
                {dropdownHasWarning ? 
                    <OverlayTrigger
                        placement={"top"}
                        overlay={<Tooltip id="tooltip-bottom" >{"Some filters haven't been applied yet."}</Tooltip>}
                    >{FilterButton}</OverlayTrigger> : FilterButton}
                {FilterMenu}
            </Dropdown>
    );
});

export { Filter };